import { useCallback, useEffect, useRef, useState } from 'react';
import { CanceledError } from 'axios';
import { getApiErrorMessage } from '../../helpers/apiError';
import {
	MAINTENANCE_PLANS_PAGE_SIZE,
	REMOTE_SUPPORT_JOB_POLL,
	REMOTE_SUPPORT_TOGGLE_ERROR_MESSAGE,
	remoteSupportFlagsFromItems
} from '../../helpers/remoteSupport';
import { describeJobOutcome, isJobSucceeded, pollJobUntilTerminal } from '../../services/jobs';
import { getMaintenancePlans, patchMaintenancePlanRemoteSupport } from '../../services/remoteSupport';

const isAbort = (controller, error) => {
	return controller.signal.aborted || error instanceof CanceledError || error?.name === 'AbortError';
};

/*
 * Walks every page of `GET /v1/licensing/maintenance-plans`. The pagination's `totalPages` says when
 * to stop; a reply without one is taken as the only page, so an older server still yields its rows.
 */
const loadAllFlags = async (signal) => {
	let page = 1;
	let totalPages = 1;
	let items = [];

	do {
		const response = await getMaintenancePlans({ page, pageSize: MAINTENANCE_PLANS_PAGE_SIZE }, { signal });
		const data = response?.data;

		items = items.concat(Array.isArray(data?.items) ? data.items : []);
		totalPages = Number.isInteger(data?.pagination?.totalPages) ? data.pagination.totalPages : 1;
		page += 1;
	} while (page <= totalPages);

	return remoteSupportFlagsFromItems(items);
};

/*
 * Owns the "Remote support" column of the Maintenance types table: `flags` is `{ [planId]: boolean }`
 * for every type the server listed, `loading` while the pages are read, and `saving` holds the ids
 * whose PATCH job has not finished. The table's rows still come from central-api; this only adds the
 * one flag that lives on rest-api, keyed by the shared id.
 *
 * `reloadKey` is the table's row list: a new list (after an add, an edit or a delete) reads the flags
 * again, which is how a freshly added type shows its server default without the table knowing it.
 *
 * `save(id, value)` shows the new value at once so the click is seen, sends the PATCH, waits for its
 * job and keeps the value only when the job succeeded. Any failure puts the old value back and
 * resolves with the message to show; success (or nothing to change) resolves with null. `toggle(id)`
 * is `save` with the opposite of the current value. The hook knows nothing about toasts.
 */
export const useMaintenancePlanRemoteSupport = (reloadKey) => {
	const [flags, setFlags] = useState({});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [saving, setSaving] = useState({});
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;

		return () => {
			mountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		const controller = new AbortController();

		setLoading(true);

		loadAllFlags(controller.signal)
			.then((loadedFlags) => {
				if (controller.signal.aborted) {
					return;
				}

				setFlags(loadedFlags);
				setError(null);
				setLoading(false);
			})
			.catch((loadError) => {
				if (isAbort(controller, loadError)) {
					return;
				}

				setFlags({});
				setError(getApiErrorMessage(loadError, REMOTE_SUPPORT_TOGGLE_ERROR_MESSAGE));
				setLoading(false);
			});

		return () => {
			controller.abort();
		};
	}, [reloadKey]);

	const setFlag = useCallback((id, value) => {
		setFlags((currentFlags) => {
			return { ...currentFlags, [id]: value };
		});
	}, []);

	const setSavingFor = useCallback((id, value) => {
		setSaving((currentSaving) => {
			const nextSaving = { ...currentSaving };

			if (value) {
				nextSaving[id] = true;
			} else {
				delete nextSaving[id];
			}

			return nextSaving;
		});
	}, []);

	const save = useCallback(async (planId, nextValue) => {
		const id = String(planId);
		const previousValue = flags[id];

		if (typeof previousValue !== 'boolean' || typeof nextValue !== 'boolean' || saving[id] || nextValue === previousValue) {
			return null;
		}

		setFlag(id, nextValue);
		setSavingFor(id, true);

		try {
			const response = await patchMaintenancePlanRemoteSupport(id, nextValue);
			const jobId = response?.data?.jobId;

			if (jobId) {
				const job = await pollJobUntilTerminal(jobId, REMOTE_SUPPORT_JOB_POLL);

				if (!isJobSucceeded(job)) {
					throw new Error(describeJobOutcome(job, {
						failureMessage: REMOTE_SUPPORT_TOGGLE_ERROR_MESSAGE,
						unappliedMessage: 'The remote support setting was not saved'
					}));
				}
			}

			return null;
		} catch (saveError) {
			if (mountedRef.current) {
				setFlag(id, previousValue);
			}

			return getApiErrorMessage(saveError, REMOTE_SUPPORT_TOGGLE_ERROR_MESSAGE);
		} finally {
			if (mountedRef.current) {
				setSavingFor(id, false);
			}
		}
	}, [flags, saving, setFlag, setSavingFor]);

	const toggle = useCallback((planId) => {
		return save(planId, !flags[String(planId)]);
	}, [flags, save]);

	return { flags, loading, error, saving, save, toggle };
};

export default useMaintenancePlanRemoteSupport;
